import React, { FC, useEffect, useState } from 'react'
import {
	Alert,
	ColumnLayout,
	Grid,
	Header,
	RadioGroup
} from '@cloudscape-design/components'
import { TagsApproval, TagsDetails } from '../../../types/tags'
import useMultiStepApprovalTags from '../../../hooks/mutate/useMultiStepApprovalTags'
import { useOktaAuth } from '@okta/okta-react'
import useUserPermissions from '../../../hooks/query/useUserPermissions'
import hasRequiredPermissions from '../../../helper/hasRequiredPermissions'
import { getRequiredApproverEmail } from '../../../helper/applicationTagApproval'
interface TagsApprovalProps {
	approvalStatus: Array<TagsApproval> | []
	isDisabled?: boolean
	tagsID?: string
	description?: string
	tagDetails?: TagsDetails['details']

}

// Entries carrying `application` belong to one application on a shared GCP Project tag
// record, so several entries can share a name. Entries without it keep matching on name.
const approvalKey = (approval: TagsApproval) =>
	approval.application ? `${approval.name}:${approval.application}` : approval.name

const persistApprovalStatus = (
	approvalList: Array<TagsApproval>,
	tagsID?: string
) => {
	if (!tagsID) {
		return
	}

	try {
		localStorage.setItem(
			`approvalStatus:tags:${tagsID}`,
			JSON.stringify(approvalList)
		)
		window.dispatchEvent(
			new CustomEvent('approvalStatusUpdated', {
				detail: approvalList
			})
		)
	} catch (e) {
		// ignore storage failures
	}

}

const ApproverOverviewLocal: FC<TagsApprovalProps> = ({
	approvalStatus,
	isDisabled = false,
	tagsID = '',
	tagDetails = {}
}) => {
	const { authState } = useOktaAuth()
	const thisUserEmail = authState?.idToken?.claims.email as string

	const { mutate: updateMultiStepTagsById } = useMultiStepApprovalTags(tagsID)
	const { data: access } = useUserPermissions(thisUserEmail)

	const canApproveForTeam = (approval: TagsApproval): boolean => {
		const permissions = access?.permissions || []
		const requiredApproverEmail = getRequiredApproverEmail(approval, tagDetails)
		if (requiredApproverEmail) {
			return (
				thisUserEmail?.toLowerCase() === requiredApproverEmail.toLowerCase()
			)
		}

		if (approval.application) {
			return false
		}

		return (
			hasRequiredPermissions([`tags:${approval.name}:*`], permissions) ||
			hasRequiredPermissions(['tags:*:approve'], permissions)
		)
	}

	const [notification, setNotification] = useState<any>({
		type: '',
		statusIconAriaLabel: '',
		msg: ''
	})
	const [showApprovalNotification, setShowApprovalNotification] =
		useState(false)

	const [approvalStatusLocal, setApprovalStatus] = useState(approvalStatus)

	useEffect(() => {
		setApprovalStatus(approvalStatus)
	}, [approvalStatus])

	useEffect(() => {
		persistApprovalStatus(approvalStatusLocal, tagsID)
	}, [approvalStatusLocal, tagsID])


	const updateApprovalStatus = (state: any, target: TagsApproval) => {
		const targetKey = approvalKey(target)
		let updatedApprovalList: Array<TagsApproval> = []
		approvalStatusLocal.forEach((item) => {
			if (approvalKey(item) === targetKey) {
				updatedApprovalList.push({
					...item,
					status:
						state.value === 'default' ? null : state.value === 'true' ? 1 : 0,
					approverID: thisUserEmail,
					lastUpdated: Date.now() + ''
				})
			} else {
				updatedApprovalList.push(item)
			}
		})

		setApprovalStatus(updatedApprovalList)

		updateMultiStepTagsById(
			{
				tagsID: tagsID,
				approvalStatus: updatedApprovalList.find(
					(item) => approvalKey(item) === targetKey
				),
				action: 'UPDATE_APPROVER_STATUS'
			},
			{
				onSuccess: () => {
					console.log('success')
					setNotification({
						type: 'success',
						statusIconAriaLabel: 'Success',
						msg: 'Tags Approval Status Updated!'
					})
					setShowApprovalNotification(true)
					setTimeout(() => {
						setShowApprovalNotification(false)
					}, 3000)
				},
				onError: (e) => {
					setNotification({
						type: 'error',
						statusIconAriaLabel: 'Error',
						msg: 'Error saving Tags, Please try again!'
					})
					setShowApprovalNotification(true)
					setTimeout(() => {
						setShowApprovalNotification(false)
					}, 3000)
				}
			}
		)
	}
	return (
		<>
			<Header variant="h3" description="">
				Approval Status
			</Header>
			<ColumnLayout columns={2} variant="text-grid">
				{approvalStatusLocal &&
					approvalStatusLocal.map((approverItem) => {
						return (
							<Grid
								disableGutters
								gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}
								key={approvalKey(approverItem)}
							>
								<div
									color="text-body-secondary"
									key={approvalKey(approverItem)}
								>
									{approverItem.name}
								</div>

								<RadioGroup
									onChange={({ detail }) => {
										updateApprovalStatus(detail, approverItem)
									}}
									value={
										approverItem.status === null
											? 'default'
											: Boolean(approverItem.status) === true
											? 'true'
											: 'false'
									}
									items={
										isDisabled
											? [
													{
														value: 'default',
														label: 'Pending Action',
														disabled: true
													},
													{
														value: 'true',
														label: 'Approved',
														disabled: true
													},
													{
														value: 'false',
														label: 'Rejected',
														disabled: true
													}
											  ]
											: [
													{
														value: 'default',
														label: 'Pending Action',
														disabled: true
													},
													{
														value: 'true',
														label: 'Approve',
														disabled: !canApproveForTeam(approverItem)
													},
													{
														value: 'false',
														label: 'Reject',
														disabled: !canApproveForTeam(approverItem)
													}
											  ]
									}
								/>
								<Grid>
									<div>
										{approverItem.status === 1 && <p>Approved By  :  {approverItem.approverID}</p>}
										{approverItem.status === 0 && <p>Rejected By :  {approverItem.approverID}</p>}
									</div>
								</Grid>
							</Grid>
						)
					})}
			</ColumnLayout>
			{showApprovalNotification && (
				<>
					<br />
					<Alert
						statusIconAriaLabel={notification.statusIconAriaLabel}
						type={notification.type}
					>
						{notification.msg}
					</Alert>
				</>
			)}
		</>
	)
}

const ApproverOverview = React.memo(ApproverOverviewLocal)
export default ApproverOverview
